/**
 * Alerts Page
 *
 * Third-party service alert management including
 * alert listing, filtering, and acknowledgment.
 */

import { useState, useEffect, useCallback } from 'react';
import { thirdPartyAnalyticsAPI } from '../services/api';
import { useEnvironment } from '../context/EnvironmentContext';
import PageHeader from '../components/analytics/PageHeader';
import MetricCard from '../components/analytics/MetricCard';
import {
  BellAlertIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';

// Format time ago
function formatTimeAgo(dateString) {
  if (!dateString) return 'Unknown';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  return 'Just now';
}

// Alert Card Component
function AlertCard({ alert, onAcknowledge, acknowledging }) {
  const severityStyles = {
    critical: 'border-l-red-500 bg-red-50',
    warning: 'border-l-yellow-500 bg-yellow-50',
    info: 'border-l-blue-500 bg-blue-50',
  };

  const severityIcons = {
    critical: <ExclamationCircleIcon className="w-5 h-5 text-red-600" />,
    warning: <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" />,
    info: <BellAlertIcon className="w-5 h-5 text-blue-600" />,
  };

  const isAcknowledged = !!alert.acknowledged_at;

  return (
    <div
      className={`border-l-4 rounded-lg p-4 ${
        isAcknowledged ? 'bg-gray-50 border-l-green-500 opacity-75' : severityStyles[alert.severity]
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          {isAcknowledged ? (
            <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <span className="flex-shrink-0 mt-0.5">
              {severityIcons[alert.severity]}
            </span>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`font-bold uppercase text-xs px-2 py-0.5 rounded ${
                isAcknowledged ? 'bg-green-100 text-green-800' :
                alert.severity === 'critical' ? 'bg-red-100 text-red-800' :
                alert.severity === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                {isAcknowledged ? 'Resolved' : alert.severity}
              </span>
              <span className="text-gray-500 text-sm">|</span>
              <span className="font-medium text-gray-700 capitalize">
                {alert.service}
              </span>
            </div>
            <p className="mt-1 text-gray-800 font-medium">
              {alert.message}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Threshold: {alert.threshold_value} | Actual: {alert.actual_value} | {formatTimeAgo(alert.created_at)}
            </p>
            {isAcknowledged && alert.acknowledged_by && (
              <p className="mt-1 text-xs text-gray-400">
                Acknowledged by {alert.acknowledged_by}
              </p>
            )}
          </div>
        </div>
        {!isAcknowledged && (
          <button
            onClick={() => onAcknowledge(alert.id)}
            disabled={acknowledging === alert.id}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex-shrink-0"
          >
            {acknowledging === alert.id ? 'Acknowledging...' : 'Acknowledge'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function AlertsPage() {
  const { currentEnv } = useEnvironment();
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acknowledging, setAcknowledging] = useState(null);

  // Filters
  const [serviceFilter, setServiceFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [unackedOnly, setUnackedOnly] = useState(true);

  // Pagination
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const pageSize = 20;

  const fetchData = useCallback(async (reset = false) => {
    setLoading(true);
    setError(null);
    try {
      const currentPage = reset ? 1 : page;
      if (reset) setPage(1);

      const filters = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      };
      if (serviceFilter !== 'all') filters.service = serviceFilter;
      if (severityFilter !== 'all') filters.severity = severityFilter;
      if (unackedOnly) filters.unacknowledged = true;

      const [alertsRes, summaryRes] = await Promise.all([
        thirdPartyAnalyticsAPI.getAlerts(filters),
        thirdPartyAnalyticsAPI.getAlertSummary(),
      ]);

      const newAlerts = alertsRes.data?.alerts || [];
      if (reset) {
        setAlerts(newAlerts);
      } else {
        setAlerts((prev) => [...prev, ...newAlerts]);
      }
      setHasMore(newAlerts.length === pageSize);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error('[AlertsPage] Error fetching data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [page, serviceFilter, severityFilter, unackedOnly]);

  useEffect(() => {
    fetchData(true);
  }, [currentEnv, serviceFilter, severityFilter, unackedOnly]);

  const handleAcknowledge = async (alertId) => {
    setAcknowledging(alertId);
    try {
      await thirdPartyAnalyticsAPI.acknowledgeAlert(alertId);
      // Refresh alerts
      fetchData(true);
    } catch (err) {
      console.error('[AlertsPage] Error acknowledging alert:', err);
      alert('Failed to acknowledge alert');
    } finally {
      setAcknowledging(null);
    }
  };

  const handleLoadMore = () => {
    setPage((p) => p + 1);
    fetchData();
  };

  // Service options
  const services = [
    'all',
    'sendgrid',
    'stripe',
    'firebase',
    'firestore',
    'rekognition',
    'r2',
    'compliance',
  ];

  // Severity options
  const severities = ['all', 'critical', 'warning', 'info'];

  if (error) {
    return (
      <div className="p-6">
        <PageHeader title="Third-Party Alerts" />
        <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800 font-medium">Error loading alerts</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button
            onClick={() => fetchData(true)}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <PageHeader
        title="Third-Party Alerts"
        description="Service health alerts and notifications"
        onRefresh={() => fetchData(true)}
        loading={loading}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <MetricCard
          title="Unacknowledged"
          value={summary?.unacknowledged || 0}
          icon={BellAlertIcon}
          status={summary?.unacknowledged > 0 ? 'warning' : 'healthy'}
          loading={loading && !summary}
        />
        <MetricCard
          title="Critical"
          value={summary?.critical || 0}
          icon={ExclamationCircleIcon}
          status={summary?.critical > 0 ? 'critical' : 'healthy'}
          loading={loading && !summary}
        />
        <MetricCard
          title="Warning"
          value={summary?.warning || 0}
          icon={ExclamationTriangleIcon}
          status={summary?.warning > 0 ? 'warning' : 'healthy'}
          loading={loading && !summary}
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <FunnelIcon className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600 font-medium">Filters:</span>
          </div>

          {/* Service Filter */}
          <select
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500"
          >
            {services.map((s) => (
              <option key={s} value={s}>
                {s === 'all' ? 'All Services' : s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>

          {/* Severity Filter */}
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500"
          >
            {severities.map((s) => (
              <option key={s} value={s}>
                {s === 'all' ? 'All Severities' : s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>

          {/* Unacknowledged Only */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={unackedOnly}
              onChange={(e) => setUnackedOnly(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600">Unacknowledged only</span>
          </label>
        </div>
      </div>

      {/* Alerts List */}
      <div className="space-y-3">
        {loading && alerts.length === 0 ? (
          // Loading skeletons
          [...Array(5)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg p-4 border border-gray-100 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))
        ) : alerts.length === 0 ? (
          <div className="bg-gray-50 rounded-xl p-12 text-center">
            <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <p className="text-gray-600 font-medium">No alerts match your filters</p>
            <p className="text-gray-400 text-sm mt-1">
              {unackedOnly ? 'All alerts have been acknowledged!' : 'Try adjusting your filter criteria'}
            </p>
          </div>
        ) : (
          alerts.map((alert) => (
            <AlertCard
              key={alert.id}
              alert={alert}
              onAcknowledge={handleAcknowledge}
              acknowledging={acknowledging}
            />
          ))
        )}
      </div>

      {/* Load More */}
      {hasMore && !loading && (
        <div className="text-center">
          <button
            onClick={handleLoadMore}
            className="px-6 py-3 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
          >
            Load More
          </button>
        </div>
      )}

      {loading && alerts.length > 0 && (
        <div className="text-center">
          <p className="text-gray-500 text-sm">Loading more...</p>
        </div>
      )}
    </div>
  );
}
